import React, { useState } from "react";

import * as Sentry from "@sentry/react";
import { difference, filter, includes, isEmpty, isEqual } from "lodash/fp";
import type { UseFormReturn } from "react-hook-form";

import Button from "@mui/material/Button";
import Collapse from "@mui/material/Collapse";
import Typography from "@mui/material/Typography";

import AuthButton from "@app/analytics/auth/components/AuthButton";
import { AUTH_PROVIDER_BUTTON_STYLE_OVERRIDES } from "@app/analytics/auth/components/styles";
import PasswordField from "@app/analytics/auth/PasswordField";
import Separator from "@app/analytics/auth/Separator";
import type { SignInFormData } from "@app/analytics/auth/SigninCard/types";
import ProviderButton from "@app/analytics/components/ProviderButton";
import TextField from "@app/analytics/components/TextField";
import useDetectedLogin from "@app/analytics/hooks/sso/useDetectedLogin";
import { AIIcon } from "@app/common/assets/icons";
import { SSO_PROVIDERS } from "@app/common/constants/providers";
import ROUTES from "@app/common/routes";
import type {
  LoginMethod,
  PersistedLogin,
  SSOProvider,
  SSOProviderId,
} from "@app/common/types";
import { isResponseErrorWithStatus } from "@app/common/utils";
import { getStringType } from "@app/utils/utils";

import InfoPanel from "@rui/InfoPanel";
import { Link } from "@rui/Link";
import { Column, Row } from "@rui/Stack";
import { Img } from "@rui/styles";

type SigninView =
  | "email_input"
  | "detected_providers"
  | "view_all_options"
  | "password_input"
  | "previous_preferred_login"
  | "error";

type LoginMethodError = "no_account_found" | "unknown";

const SSO_PROVIDER_VIEWS: SigninView[] = [
  "detected_providers",
  "view_all_options",
  "previous_preferred_login",
  "error",
];

const CAN_VIEW_ALL_OPTIONS_VIEWS: SigninView[] = [
  "email_input",
  "detected_providers",
];

const ChangeViewButton = ({
  children,
  onClick,
  show,
  separator,
}: {
  children: React.ReactNode;
  onClick: () => void;
  show: boolean;
  separator?: "above" | "below";
}) => (
  <Collapse in={show} mountOnEnter unmountOnExit>
    {separator === "above" && <Separator>or</Separator>}
    <Row variant="center">
      <Button color="secondary" onClick={onClick}>
        {children}
      </Button>
    </Row>
    {separator === "below" && <Separator>or</Separator>}
  </Collapse>
);

const getInitialView = (persistedLogin: PersistedLogin | null): SigninView => {
  if (persistedLogin?.method) {
    return persistedLogin.method === "password"
      ? "password_input"
      : "previous_preferred_login";
  }
  return "email_input";
};

const IdentifyFirstSignIn = ({
  form,
  isLoading,
  onOAuthClick,
  persistedLogin,
  standalone,
}: {
  form: UseFormReturn<SignInFormData>;
  isLoading: boolean;
  onOAuthClick: () => void;
  persistedLogin: PersistedLogin | null;
  standalone: boolean;
}) => {
  const {
    formState: { errors },
  } = form;

  const { getLoginMethods } = useDetectedLogin();

  const [detectedLoginMethods, setDetectedLoginMethods] = useState<
    LoginMethod[] | null
  >(null);

  const [view, setView] = useState<SigninView>(getInitialView(persistedLogin));
  const [error, setError] = useState<LoginMethodError | null>(null);

  const handleErrorResponse = (error?: unknown) => {
    setDetectedLoginMethods([]);
    setView("error");
    setError(
      error && isResponseErrorWithStatus(error, 404)
        ? "no_account_found"
        : "unknown"
    );
  };

  const populateLoginMethods = async (email: string) => {
    getLoginMethods.mutate(
      { email },
      {
        onSuccess: (methods) => {
          if (isEmpty(methods)) {
            // shouldn't ever happen, but handle just in case
            Sentry.captureMessage("No login methods detected for user");
            return handleErrorResponse();
          }

          // 1. save the detected login methods
          setDetectedLoginMethods(methods);
          // 2. clear any previous error
          setError(null);
          // 3. push the user to the right view
          if (isEqual(methods, ["password"])) {
            setView("password_input");
          } else {
            setView("detected_providers");
          }
        },
        onError: handleErrorResponse,
      }
    );
  };

  const handleSubmitEmail = async () => {
    const email = form.getValues("email");
    const isValid = await form.trigger("email");
    if (isValid) {
      return populateLoginMethods(email ?? "");
    }
  };

  const handleSignIn = () => {
    form.setValue("action", "email");
  };

  const handleClickOAuth = (providerId: SSOProviderId) => {
    form.setValue("action", providerId);
    onOAuthClick();
  };

  const hasNoDetectedLoginMethods =
    detectedLoginMethods != null && isEmpty(detectedLoginMethods);
  const canOnlySignInWithPassword = isEqual(["password"], detectedLoginMethods);

  const handleChangeEmail = () => {
    setView("email_input");
    setError(null);
  };
  const handleClickSigninWithPassword = () => setView("password_input");
  const handleClickUseSingleSignOn = () =>
    setView(
      hasNoDetectedLoginMethods ? "view_all_options" : "detected_providers"
    );
  const handleClickViewAllOptions = () => setView("view_all_options");

  const onEmailInput = view === "email_input";
  const onPasswordInput = view === "password_input";

  const showInfoPanel =
    !persistedLogin?.method &&
    (view === "detected_providers" || view === "password_input") &&
    !canOnlySignInWithPassword;
  const showEmailInput = view !== "view_all_options" && view !== "error";
  const showSSOProviders = includes(view, SSO_PROVIDER_VIEWS);
  const showViewAllOptions = includes(view, CAN_VIEW_ALL_OPTIONS_VIEWS);
  // show the "Use SSO" button if they're on the password input and have at
  // least 1 supported SSO method
  const showUseSSO =
    onPasswordInput &&
    (hasNoDetectedLoginMethods ||
      !isEmpty(difference(detectedLoginMethods, ["password"])));

  const showSignInWithEmailPass =
    !onEmailInput &&
    !onPasswordInput &&
    (includes("password", detectedLoginMethods) ||
      view === "view_all_options" ||
      view === "error");

  const detectedSSOProviders = detectedLoginMethods
    ? filter(({ id }) => includes(id, detectedLoginMethods), SSO_PROVIDERS)
    : [];

  const preferredSSOLoginMethod = persistedLogin?.method
    ? (filter({ id: persistedLogin.method }, SSO_PROVIDERS) as SSOProvider[])
    : [];

  const ssoProvidersToShow =
    view === "previous_preferred_login"
      ? preferredSSOLoginMethod
      : view === "detected_providers"
        ? detectedSSOProviders
        : showSSOProviders
          ? SSO_PROVIDERS
          : [];

  const textFieldIsReadOnly = !onEmailInput;

  return (
    <Column>
      <Collapse in={error === "no_account_found"}>
        <InfoPanel severity="warning" sx={{ mb: 2 }}>
          Please{" "}
          <Link underline to={ROUTES.SIGN_UP}>
            create an account
          </Link>{" "}
          or sign in with another method to continue
        </InfoPanel>
      </Collapse>
      <Collapse in={showInfoPanel}>
        <InfoPanel
          severity="info"
          contentVariant="custom"
          icon={<AIIcon />}
          sx={{ mb: 2 }}
        >
          <Typography variant="bodySecondaryMed" color="info.main">
            Next time we'll remember your sign-in preference to make signing in
            quicker and easier!
          </Typography>
        </InfoPanel>
      </Collapse>
      <ChangeViewButton
        show={showUseSSO}
        onClick={handleClickUseSingleSignOn}
        separator="below"
      >
        Use Single Sign-On (SSO)
      </ChangeViewButton>
      <Collapse in={showEmailInput} unmountOnExit mountOnEnter>
        <Column spacing={2} mb={2} px={{ sm: 4 }}>
          <Column spacing={1}>
            <TextField
              data-test-id="auth-email-input"
              label="Email address"
              InputProps={{
                ...form.register("email"),
                readOnly: textFieldIsReadOnly,
                disabled: getLoginMethods.isPending || textFieldIsReadOnly,
                endAdornment: textFieldIsReadOnly && (
                  <Link
                    variant="bodySecondaryReg"
                    underline
                    onClick={handleChangeEmail}
                  >
                    Change
                  </Link>
                ),
              }}
              error={!!errors.email}
              onEnter={handleSubmitEmail}
              helperText={getStringType(errors.email?.message)}
              sx={{
                "&:not(.Mui-disabled) .MuiInputBase-readOnly": {
                  backgroundColor: "background.default",
                  WebkitTextFillColor: "initial !important",
                  color: "shades.neutral.800",
                },
              }}
            />
            <Collapse in={onPasswordInput} unmountOnExit mountOnEnter>
              <PasswordField
                label="Password"
                InputProps={{ ...form.register("password") }}
                error={!!errors.password}
                helperText={getStringType(errors.password?.message)}
              />
              {!standalone && (
                <Row variant="right" mt={0.5}>
                  <Link
                    variant="labelReg"
                    to={ROUTES.FORGOT_PASSWORD}
                    data-test-id="auth-forgot-password"
                  >
                    Forgot password?
                  </Link>
                </Row>
              )}
            </Collapse>
          </Column>
          <Collapse in={onEmailInput} unmountOnExit mountOnEnter>
            <AuthButton
              data-test-id="auth-button-continue"
              onClick={handleSubmitEmail}
              loading={getLoginMethods.isPending}
            >
              Continue
            </AuthButton>
          </Collapse>
          <Collapse in={onPasswordInput} unmountOnExit mountOnEnter>
            <AuthButton
              data-test-id="auth-button-sign-in"
              onClick={handleSignIn}
              loading={isLoading}
              type="submit"
            >
              Sign in
            </AuthButton>
          </Collapse>
        </Column>
      </Collapse>
      <Collapse in={showSSOProviders} unmountOnExit mountOnEnter>
        <Column
          spacing={1}
          px={{ sm: 4 }}
          pb={showSignInWithEmailPass && !showViewAllOptions ? 0 : 2}
        >
          {ssoProvidersToShow.map((provider) => (
            <ProviderButton
              key={provider.id}
              icon={
                <Img
                  src={provider.icon}
                  alt={`Sign in with ${provider.displayName}`}
                />
              }
              providerId={provider.id}
              onClick={() => handleClickOAuth(provider.id)}
              sx={AUTH_PROVIDER_BUTTON_STYLE_OVERRIDES}
              type="submit"
            >
              Sign in with {provider.displayName}
            </ProviderButton>
          ))}
        </Column>
      </Collapse>
      <ChangeViewButton
        onClick={handleClickViewAllOptions}
        show={showViewAllOptions}
      >
        View all options
      </ChangeViewButton>
      <ChangeViewButton
        onClick={handleClickSigninWithPassword}
        show={showSignInWithEmailPass}
        separator="above"
      >
        Sign in with email and password
      </ChangeViewButton>
    </Column>
  );
};

export default IdentifyFirstSignIn;
